import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
// KAMPANYA TAKİP: useCampaignTracking hook'unu import et
import useCampaignTracking from '../hooks/useCampaignTracking';

const CampaignTrackingPage = () => {
    const [campaignData, setCampaignData] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    
    // KAMPANYA TAKİP: useCampaignTracking hook'unu kullan
    const { 
        campaignData: realtimeCampaignData, 
        realtimeUpdates,
        connected: websocketConnected,
        error: websocketError 
    } = useCampaignTracking();

    useEffect(() => {
        fetchCampaignData();
        fetchStatistics();
    }, [filterType, filterStatus, currentPage]);

    // KAMPANYA TAKİP: Gerçek zamanlı veriler geldiğinde state'i güncelle
    useEffect(() => {
        if (realtimeCampaignData.statistics) {
            setStatistics(realtimeCampaignData.statistics);
        }
        if (realtimeCampaignData.history && realtimeCampaignData.history.length > 0) {
            setCampaignData(prev => ({
                ...prev,
                campaignHistory: realtimeCampaignData.history
            }));
        }
    }, [realtimeCampaignData]);

    const fetchCampaignData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/user-campaign-tracking/campaign-history', {
                params: { 
                    campaign_type: filterType,
                    status: filterStatus,
                    page: currentPage,
                    limit: 10
                }
            });
            
            if (response.data.success) {
                setCampaignData(response.data.data);
                setError(null);
            }
        } catch (error) {
            console.error('Kampanya geçmişi yüklenirken hata:', error);
            setError('Kampanya geçmişi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const response = await api.get('/api/user-campaign-tracking/statistics');
            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
        }
    };

    if (loading && !campaignData) {
        return <CampaignHistoryLoader />;
    }

    if (error) {
        return <ErrorMessage message={error} onRetry={fetchCampaignData} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 text-center"
                >
                    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                        🏷️ Kampanya Takip Merkezi
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Kampanya geçmişinizi takip edin ve tasarruflarınızı görün
                    </p>
                    {/* KAMPANYA TAKİP: WebSocket bağlantı durumu */}
                    <div className="mt-2 flex items-center justify-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${websocketConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm text-gray-500">
                            {websocketConnected ? 'Gerçek zamanlı bağlantı aktif' : 'Gerçek zamanlı bağlantı yok'}
                        </span>
                    </div>
                </motion.div>

                {/* Statistics Cards */}
                {statistics && <CampaignStatisticsCards statistics={statistics} />}

                {/* Filters */}
                <CampaignFilters 
                    filterType={filterType}
                    setFilterType={setFilterType}
                    filterStatus={filterStatus}
                    setFilterStatus={setFilterStatus}
                />

                {/* Campaign History List */}
                <CampaignHistoryList 
                    campaignHistory={campaignData?.campaignHistory}
                    loading={loading}
                />

                {/* Pagination */}
                {campaignData?.pagination && (
                    <Pagination 
                        pagination={campaignData.pagination}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                    />
                )}
            </div>
        </div>
    );
};

// İstatistik Kartları Komponenti
const CampaignStatisticsCards = ({ statistics }) => {
    const cards = [
        {
            title: 'Toplam Tasarruf',
            value: statistics?.totalSavings || 0,
            type: 'currency',
            icon: '💰',
            gradient: 'from-green-500 to-emerald-600',
            bgPattern: 'bg-pattern-dots'
        },
        {
            title: 'Kullanılan Kampanya',
            value: statistics?.totalCampaignsUsed || 0,
            type: 'number',
            icon: '🏷️',
            gradient: 'from-blue-500 to-cyan-600',
            bgPattern: 'bg-pattern-zigzag'
        },
        {
            title: 'En Çok Kullanılan',
            value: getCampaignTypeDisplayName(statistics?.mostUsedCampaignType) || 'Henüz yok',
            type: 'text',
            icon: '⭐',
            gradient: 'from-purple-500 to-pink-600',
            bgPattern: 'bg-pattern-triangles'
        },
        {
            title: 'Bu Ay Tasarruf',
            value: statistics?.monthlyStats?.savings || 0,
            type: 'currency',
            icon: '📈',
            gradient: 'from-orange-500 to-red-600',
            bgPattern: 'bg-pattern-waves'
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {cards.map((card, index) => (
                <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ 
                        delay: index * 0.1, 
                        type: "spring", 
                        stiffness: 300,
                        damping: 20
                    }}
                    className={`relative bg-gradient-to-br ${card.gradient} rounded-2xl p-6 text-white overflow-hidden group cursor-pointer`}
                >
                    {/* Background Pattern */}
                    <div className={`absolute inset-0 opacity-10 ${card.bgPattern}`}></div>
                    
                    {/* Animated Background */}
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
                        animate={{ 
                            background: [
                                'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
                                'linear-gradient(225deg, rgba(255,255,255,0.1) 0%, transparent 100%)'
                            ]
                        }}
                        transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
                    />

                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <motion.div 
                                className="text-3xl"
                                whileHover={{ rotate: 360, scale: 1.2 }}
                                transition={{ duration: 0.5 }}
                            >
                                {card.icon}
                            </motion.div>
                            <div className="bg-white bg-opacity-20 rounded-lg p-2 backdrop-blur-sm">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                </svg>
                            </div>
                        </div>
                        
                        <h3 className="text-sm font-medium opacity-90 mb-2">
                            {card.title}
                        </h3>
                        
                        <motion.p 
                            className="text-2xl md:text-3xl font-bold"
                            key={card.value}
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500 }}
                        >
                            {card.type === 'currency' ? 
                                formatCurrency(card.value) :
                                card.value
                            }
                        </motion.p>

                        {/* Sparkle Effect */}
                        <div className="absolute top-2 right-2">
                            {[...Array(3)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-1 h-1 bg-white rounded-full"
                                    style={{
                                        top: `${10 + i * 15}px`,
                                        right: `${10 + i * 8}px`
                                    }}
                                    animate={{
                                        opacity: [0, 1, 0],
                                        scale: [0, 1, 0]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        delay: i * 0.3
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

// Filtre Komponenti
const CampaignFilters = ({ filterType, setFilterType, filterStatus, setFilterStatus }) => {
    const campaignTypes = [
        { value: 'all', label: 'Tüm Kampanyalar', icon: '🎯' },
        { value: 'cart', label: 'Sepet Kampanyası', icon: '🛒' },
        { value: 'category', label: 'Kategori İndirimi', icon: '📂' },
        { value: 'flash_sale', label: 'Flash Sale', icon: '⚡' },
        { value: 'coupon', label: 'Kupon', icon: '🎫' },
        { value: 'package', label: 'Paket Kampanya', icon: '📦' },
        { value: 'special_day', label: 'Özel Gün', icon: '🎉' }
    ];

    const statusOptions = [
        { value: 'all', label: 'Tüm Durumlar', icon: '📊' },
        { value: 'active', label: 'Aktif', icon: '✅' },
        { value: 'refunded', label: 'İade Edildi', icon: '↩️' },
        { value: 'partial_refund', label: 'Kısmi İade', icon: '⚠️' },
        { value: 'expired', label: 'Süresi Dolmuş', icon: '⏰' }
    ];

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-white rounded-2xl shadow-lg p-6"
        >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Filtreler</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Campaign Type Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kampanya Türü
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {campaignTypes.map(type => (
                            <motion.button
                                key={type.value}
                                onClick={() => setFilterType(type.value)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    filterType === type.value
                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <span className="block text-lg mb-1">{type.icon}</span>
                                {type.label}
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* Status Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Durum
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {statusOptions.map(status => (
                            <motion.button
                                key={status.value}
                                onClick={() => setFilterStatus(status.value)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    filterStatus === status.value
                                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <span className="block text-lg mb-1">{status.icon}</span>
                                {status.label}
                            </motion.button>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Kampanya Geçmişi Listesi
const CampaignHistoryList = ({ campaignHistory, loading }) => {
    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded mb-4"></div>
                        <div className="h-8 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (!campaignHistory || campaignHistory.length === 0) {
        return (
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
            >
                <div className="text-6xl mb-4">🎪</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Henüz kampanya geçmişiniz yok
                </h3>
                <p className="text-gray-600 mb-6">
                    İlk kampanyanızdan yararlanmaya başlayın!
                </p>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-shadow"
                    onClick={() => window.location.href = '/'}
                >
                    Kampanyaları Keşfet
                </motion.button>
            </motion.div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <AnimatePresence mode="wait">
                {campaignHistory.map((campaign, index) => (
                    <CampaignHistoryCard 
                        key={campaign.id} 
                        campaign={campaign} 
                        index={index}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

// Kampanya Kartı Komponenti
const CampaignHistoryCard = ({ campaign, index }) => {
    const getStatusInfo = (status) => {
        const statusMap = {
            active: { color: 'text-green-600', bg: 'bg-green-100', icon: '✅', label: 'Aktif' },
            refunded: { color: 'text-red-600', bg: 'bg-red-100', icon: '↩️', label: 'İade Edildi' },
            partial_refund: { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: '⚠️', label: 'Kısmi İade' },
            expired: { color: 'text-gray-600', bg: 'bg-gray-100', icon: '⏰', label: 'Süresi Doldu' }
        };
        return statusMap[status] || statusMap.active;
    };

    const statusInfo = getStatusInfo(campaign.status);
    const campaignTypeIcon = getCampaignTypeIcon(campaign.campaign_type);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            whileHover={{ y: -5, scale: 1.02 }}
            transition={{ 
                delay: index * 0.1,
                type: "spring",
                stiffness: 300,
                damping: 20
            }}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
        >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4">
                <div className="flex items-center justify-between text-white">
                    <div className="flex items-center space-x-2">
                        <span className="text-2xl">{campaignTypeIcon}</span>
                        <span className="font-medium">
                            {getCampaignTypeDisplayName(campaign.campaign_type)}
                        </span>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.icon} {statusInfo.label}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">
                    {campaign.campaigns?.name || 'Kampanya'}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {campaign.campaigns?.description || 'Kampanya açıklaması bulunamadı'}
                </p>

                {/* Discount Amount */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Toplam İndirim</span>
                        <span className="text-2xl font-bold text-green-600">
                            {formatCurrency(campaign.original_discount_amount)}
                        </span>
                    </div>
                    
                    {campaign.refund_amount > 0 && (
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">İade Edilen</span>
                            <span className="text-red-600 font-medium">
                                -{formatCurrency(campaign.refund_amount)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Order Info */}
                {campaign.orders && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span>📦</span>
                            <span>Sipariş: {campaign.orders.order_number}</span>
                        </div>
                    </div>
                )}

                {/* Date */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>📅 {formatDate(campaign.usage_date)}</span>
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="text-purple-600 hover:text-purple-800"
                    >
                        Detay →
                    </motion.button>
                </div>
            </div>

            {/* Hover Effect */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
            />
        </motion.div>
    );
};

// Pagination Komponenti
const Pagination = ({ pagination, currentPage, setCurrentPage }) => {
    const { page, limit, total } = pagination;
    const totalPages = Math.ceil(total / limit);

    if (totalPages <= 1) return null;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center space-x-2 mb-8"
        >
            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-4 py-2 rounded-lg bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
                ← Önceki
            </motion.button>

            <div className="flex space-x-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                        <motion.button
                            key={pageNum}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-lg font-medium ${
                                pageNum === currentPage
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                                    : 'bg-white text-gray-700 shadow-md hover:bg-gray-50'
                            }`}
                        >
                            {pageNum}
                        </motion.button>
                    );
                })}
            </div>

            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 rounded-lg bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Sonraki →
            </motion.button>
        </motion.div>
    );
};

// Loading Komponenti
const CampaignHistoryLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
            <motion.div
                className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <motion.h3 
                className="text-lg font-medium text-gray-700"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
            >
                Kampanya geçmişiniz yükleniyor...
            </motion.h3>
        </motion.div>
    </div>
);

// Error Komponenti
const ErrorMessage = ({ message, onRetry }) => (
    <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50"
    >
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
            <div className="text-6xl mb-4">😔</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Bir hata oluştu</h3>
            <p className="text-gray-600 mb-6">{message}</p>
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onRetry}
                className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-shadow"
            >
                Tekrar Dene
            </motion.button>
        </div>
    </motion.div>
);

// Yardımcı Fonksiyonlar
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2
    }).format(amount || 0);
};

const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(new Date(dateString));
};

const getCampaignTypeIcon = (type) => {
    const icons = {
        cart: '🛒',
        category: '📂',
        flash_sale: '⚡',
        coupon: '🎫',
        package: '📦',
        special_day: '🎉'
    };
    return icons[type] || '🏷️';
};

const getCampaignTypeDisplayName = (type) => {
    const names = {
        cart: 'Sepet Kampanyası',
        category: 'Kategori İndirimi',
        flash_sale: 'Flash Sale',
        coupon: 'Kupon Kampanyası',
        package: 'Paket Kampanya',
        special_day: 'Özel Gün Kampanyası'
    };
    return names[type] || 'Bilinmeyen';
};

export default CampaignTrackingPage;